import { Settings2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { getEufyStatus, getEufySummary } from '@/api/services/eufy'
import { getBodyProfile, updateBodyProfile } from '@/api/services/nutrition'
import type { EufyCustomer } from '@/api/types/eufy'
import type { BodyGender, BodyGoal } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'

const GOAL_OPTIONS: { value: BodyGoal; label: string }[] = [
  { value: 'lean_bulk', label: 'リーンバルク（増量）' },
  { value: 'cut', label: '減量' },
  { value: 'maintain', label: '維持' },
  { value: 'auto_switch', label: '自動 (体脂肪率連動)' },
]

const GENDER_OPTIONS: { value: BodyGender; label: string }[] = [
  { value: 'male', label: '男性' },
  { value: 'female', label: '女性' },
]

function parseOptionalNumber(value: string, parser: (s: string) => number): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = parser(trimmed)
  return Number.isFinite(n) ? n : null
}

type BodyProfileDialogProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onSaved?: () => void
  required?: boolean
}

export function BodyProfileDialog({
  open: controlledOpen,
  onOpenChange,
  onSaved,
  required,
}: BodyProfileDialogProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen
  const [loading, setLoading] = useState(false)

  const [heightCm, setHeightCm] = useState('')
  const [weightKg, setWeightKg] = useState('')
  const [age, setAge] = useState('')
  const [gender, setGender] = useState<BodyGender>('male')
  const [goal, setGoal] = useState<BodyGoal>('maintain')
  const [autoAdjust, setAutoAdjust] = useState(false)
  const [autoCutThreshold, setAutoCutThreshold] = useState('15')
  const [autoBulkThreshold, setAutoBulkThreshold] = useState('10')
  const [eufyCustomerId, setEufyCustomerId] = useState<string | null>(null)
  const [eufyCustomers, setEufyCustomers] = useState<EufyCustomer[]>([])
  const [garminLinked, setGarminLinked] = useState(false)
  const handleEufyCustomerChange = (customerId: string | null) => {
    setEufyCustomerId(customerId)
    if (!customerId) return
    const customer = eufyCustomers.find((c) => c.customer_id === customerId)
    if (!customer) return
    if (customer.height_cm) setHeightCm(customer.height_cm.toString())
    if (customer.sex) setGender(customer.sex.toLowerCase() === 'female' ? 'female' : 'male')
    if (customer.birthday) {
      const birthDate = new Date(customer.birthday * 1000)
      const today = new Date()
      let calculatedAge = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--
      }
      if (calculatedAge > 0) setAge(calculatedAge.toString())
    }
    // 直近の体重を取得
    getEufySummary(customerId)
      .then((summary) => {
        if (summary.latest?.weight_kg) {
          setWeightKg(summary.latest.weight_kg.toString())
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (!open) return
    setLoading(true)
    Promise.all([getBodyProfile(), getEufyStatus().catch(() => null)])
      .then(([p, eufyStatus]) => {
        setHeightCm(p.height_cm?.toString() ?? '')
        setWeightKg(p.weight_kg?.toString() ?? '')
        setAge(p.age?.toString() ?? '')
        setGender(p.gender)
        setGoal(p.goal)
        setAutoAdjust(p.auto_adjust)
        setAutoCutThreshold(p.auto_cut_threshold.toString())
        setAutoBulkThreshold(p.auto_bulk_threshold.toString())
        setEufyCustomerId(p.eufy_customer_id)
        setGarminLinked(p.garmin_linked)
        if (eufyStatus?.customers) {
          setEufyCustomers(eufyStatus.customers)
        }
      })
      .catch(() => toast.error('プロフィール取得に失敗'))
      .finally(() => setLoading(false))
  }, [open])

  const handleSave = async () => {
    const parsedWeight = parseOptionalNumber(weightKg, parseFloat)
    const parsedHeight = parseOptionalNumber(heightCm, parseFloat)
    const parsedAge = parseOptionalNumber(age, (s) => parseInt(s, 10))
    if (!parsedWeight || !parsedHeight || !parsedAge) {
      toast.error('身長・体重・年齢は必須です')
      return
    }
    setLoading(true)
    try {
      await updateBodyProfile({
        height_cm: parseOptionalNumber(heightCm, parseFloat),
        weight_kg: parseOptionalNumber(weightKg, parseFloat),
        age: parseOptionalNumber(age, (s) => parseInt(s, 10)),
        gender,
        goal,
        auto_adjust: autoAdjust,
        auto_cut_threshold: parseFloat(autoCutThreshold) || undefined,
        auto_bulk_threshold: parseFloat(autoBulkThreshold) || undefined,
        eufy_customer_id: eufyCustomerId,
        garmin_linked: garminLinked,
      })
      toast.success('プロフィールを更新しました')
      setOpen(false)
      onSaved?.()
    } catch {
      toast.error('更新に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon">
            <Settings2 className="size-5" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        className="max-w-sm"
        {...(required && {
          onInteractOutside: (e: Event) => e.preventDefault(),
          onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault(),
          hideCloseButton: true,
        })}
      >
        <DialogHeader>
          <DialogTitle>身体プロフィール</DialogTitle>
          <DialogDescription>
            {required ? '栄養管理を利用するには身長・体重・年齢の設定が必要です' : 'カロリー目標の計算に使用します'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Label className="w-20 shrink-0">身長 (cm)</Label>
            <Input type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} placeholder="182" />
          </div>
          <div className="flex items-center gap-3">
            <Label className="w-20 shrink-0">体重 (kg)</Label>
            <Input type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder="75" />
          </div>
          <div className="flex items-center gap-3">
            <Label className="w-20 shrink-0">年齢</Label>
            <Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="30" />
          </div>
          <div className="flex items-center gap-3">
            <Label className="w-20 shrink-0">性別</Label>
            <Select value={gender} onValueChange={(v) => setGender(v as BodyGender)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Label className="w-20 shrink-0">目標</Label>
            <Select value={goal} onValueChange={(v) => setGoal(v as BodyGoal)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GOAL_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>カロリー自動調整</Label>
              <p className="text-muted-foreground text-xs">体脂肪率の変動に応じて目標を自動補正</p>
            </div>
            <Switch checked={autoAdjust} onCheckedChange={setAutoAdjust} />
          </div>

          {goal === 'auto_switch' && (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-xs font-medium">自動目標の切替閾値 (体脂肪率%)</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Label className="shrink-0 text-xs">減量開始</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={autoCutThreshold}
                    onChange={(e) => setAutoCutThreshold(e.target.value)}
                    placeholder="15"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="shrink-0 text-xs">増量開始</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={autoBulkThreshold}
                    onChange={(e) => setAutoBulkThreshold(e.target.value)}
                    placeholder="10"
                  />
                </div>
              </div>
              <p className="text-muted-foreground text-[10px]">
                BF {'>'} 減量開始 → cut / BF {'<'} 増量開始 → lean_bulk
              </p>
            </div>
          )}

          {eufyCustomers.length > 0 && (
            <div className="flex items-center gap-3">
              <Label className="w-20 shrink-0">Eufy連携</Label>
              <Select
                value={eufyCustomerId ?? 'none'}
                onValueChange={(v) => handleEufyCustomerChange(v === 'none' ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="未選択" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">未選択</SelectItem>
                  {eufyCustomers.map((c) => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.name || c.customer_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Garmin連携</Label>
              <p className="text-muted-foreground text-xs">歩数データを活動係数の計算に使用</p>
            </div>
            <Switch checked={garminLinked} onCheckedChange={setGarminLinked} />
          </div>

          <Button className="w-full" onClick={handleSave} disabled={loading}>
            保存
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
